import React, { useState, useEffect } from 'react';
import apiService from '../services/apiService';

function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    full_name: '',
    role: 'staff',
    is_active: true
  });
  const token = localStorage.getItem('token');

  useEffect(() => {
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadUsers = () => {
    apiService.getUsers(token)
      .then(response => setUsers(response.data.users))
      .catch(error => console.error('Error loading users:', error));
  };

  const handleAdd = () => {
    setEditingUser(null);
    setFormData({
      username: '',
      email: '',
      password: '',
      full_name: '',
      role: 'staff',
      is_active: true
    });
    setShowForm(true);
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      username: user.username || '',
      email: user.email || '',
      password: '', // Don't pre-fill password
      full_name: user.full_name || '',
      role: user.role || 'staff',
      is_active: user.is_active ? true : false
    });
    setShowForm(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (editingUser) {
      // Update existing user
      const updateData = {
        ...formData,
        id: editingUser.id
      };
      
      // Remove password from update if not provided
      if (!updateData.password || updateData.password.trim() === '') {
        delete updateData.password;
      }
      
      apiService.updateUser(updateData, token)
        .then(() => {
          loadUsers();
          setShowForm(false);
          setEditingUser(null);
          setFormData({
            username: '',
            email: '',
            password: '',
            full_name: '',
            role: 'staff',
            is_active: true
          });
        })
        .catch(error => {
          console.error('Error updating user:', error);
          alert(error.response?.data?.message || 'Failed to update user. Please try again.');
        });
    } else {
      // Create new user
      if (!formData.password || formData.password.trim() === '') {
        alert('Password is required for new users.');
        return;
      }
      
      apiService.createUser(formData, token)
        .then(() => {
          loadUsers();
          setShowForm(false);
          setFormData({
            username: '',
            email: '',
            password: '',
            full_name: '',
            role: 'staff',
            is_active: true
          });
        })
        .catch(error => {
          console.error('Error creating user:', error);
          alert(error.response?.data?.message || 'Failed to create user. Please try again.');
        });
    }
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      apiService.deleteUser(id, token)
        .then(() => loadUsers())
        .catch(error => {
          console.error('Error deleting user:', error);
          alert(error.response?.data?.message || 'Failed to delete user. Please try again.');
        });
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingUser(null);
    setFormData({
      username: '',
      email: '',
      password: '',
      full_name: '',
      role: 'staff',
      is_active: true
    });
  };

  return (
    <div>
      <h2>User Management</h2>
      
      <button className="btn-admin btn-add" onClick={handleAdd}>
        + Add New User
      </button>

      {showForm && (
        <div className="modal-overlay" onClick={handleCancel}>
          <div className="modal-content admin-form" onClick={(e) => e.stopPropagation()}>
            <h3>{editingUser ? 'Edit User' : 'Add New User'}</h3>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Username *</label>
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  required
                  placeholder="Enter username"
                />
              </div>

              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  placeholder="Enter email address"
                />
              </div>

              <div className="form-group">
                <label>
                  Password {editingUser ? '(leave blank to keep current)' : '*'}
                </label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required={!editingUser}
                  placeholder={editingUser ? "Enter new password (optional)" : "Enter password"}
                  minLength={editingUser ? 0 : 6}
                />
              </div>

              <div className="form-group">
                <label>Full Name</label>
                <input
                  type="text"
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  placeholder="Enter full name"
                />
              </div>

              <div className="form-group">
                <label>Role *</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  required
                >
                  <option value="staff">Staff</option>
                  <option value="manager">Manager</option>
                  <option value="admin">Admin</option>
                </select>
              </div>

              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                  Active
                </label>
              </div>

              <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                <button type="submit" className="btn-save">
                  {editingUser ? 'Update' : 'Add'} User
                </button>
                <button type="button" className="btn-admin btn-delete" onClick={handleCancel}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      <div className="admin-card">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Username</th>
              <th>Email</th>
              <th>Full Name</th>
              <th>Role</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.length === 0 ? (
              <tr>
                <td colSpan="6" style={{ textAlign: 'center', padding: '40px' }}>
                  No users found. Click "Add New User" to create one.
                </td>
              </tr>
            ) : (
              users.map(user => (
                <tr key={user.id}>
                  <td>{user.username}</td>
                  <td>{user.email}</td>
                  <td>{user.full_name || '-'}</td>
                  <td style={{ textTransform: 'capitalize' }}>{user.role || 'staff'}</td>
                  <td>{user.is_active ? 'Active' : 'Inactive'}</td>
                  <td>
                    <button 
                      className="btn-admin btn-edit" 
                      onClick={() => handleEdit(user)}
                      style={{ marginRight: '10px' }}
                    >
                      Edit
                    </button>
                    <button 
                      className="btn-admin btn-delete" 
                      onClick={() => handleDelete(user.id)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default AdminUsers;
